import { useEffect, useMemo, useState } from "react";
import JornadaTrabalhadaService from "../../services/resource/jornadaTrabalhadaService";

function JornadaFuncionarioList({ funcionarioId, dataInicial, dataFinal, onClose }) {
    const jornadaTrabalhadaService = new JornadaTrabalhadaService();

    const [jornadas, setJornadas] = useState([]);
    const [loading, setLoading] = useState(true);

    const [expandido, setExpandido] = useState(null);

    const [mesSelecionado, setMesSelecionado] = useState(() => {
        const hoje = new Date();

        return {
            ano: hoje.getFullYear(),
            mes: hoje.getMonth() + 1
        };
    });

    const primeiroDiaMes = (ano, mes) => {
        return `${ano}-${String(mes).padStart(2, "0")}-01`;
    };

    const ultimoDiaMes = (ano, mes) => {
        return new Date(ano, mes, 0)
            .toISOString()
            .split("T")[0];
    };

    // ── Utilitários ──────────────────────────────────────────────────────────
    const mth = (minutos = 0) => {
        if (minutos === 0) return "—";
        const neg = minutos < 0;
        const abs = Math.abs(minutos);
        const h = Math.floor(abs / 60);
        const m = abs % 60;
        return `${neg ? "-" : ""}${h}h ${m}min`;
    };

    const fmtData = (iso) => {
        if (!iso) return "—";
        const [y, mo, d] = iso.split("-");
        return `${d}/${mo}/${y}`;
    };

    const diaSemana = (iso) => {
        if (!iso) return "";
        const dias = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
        return dias[new Date(iso + "T12:00:00").getDay()];
    };

    const mes = (iso) => {
        if (!iso) return "";

        const meses = [
            "Jan", "Fev", "Mar", "Abr",
            "Mai", "Jun", "Jul", "Ago",
            "Set", "Out", "Nov", "Dez"
        ];

        return meses[new Date(iso + "T12:00:00").getMonth()];
    };

    const corSaldo = (v = 0) =>
        v > 0 ? "text-emerald-600" : v < 0 ? "text-red-500" : "text-muted-foreground";

    const bgSaldo = (v = 0) =>
        v > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : v < 0 ? "bg-red-50 border-red-200 text-red-600"
                : "bg-muted border-border text-muted-foreground";

    const tipoMovimentoBadge = (tipo) => {
        const map = {
            NORMAL: "bg-muted text-muted-foreground",
            FALTA: "bg-red-50 text-red-500",
            FERIADO: "bg-violet-50 text-violet-500",
            AJUSTE: "bg-amber-50 text-amber-600",
            SOBREAVISO: "bg-sky-50 text-sky-600",
            AFASTAMENTO: "bg-orange-50 text-orange-500",
        };
        return map[tipo] ?? "bg-muted text-muted-foreground";
    };

    useEffect(() => {

        setLoading(true);

        jornadaTrabalhadaService.listaJornadaPorFuncionario({
            funcionario: funcionarioId,
            dataInicial: primeiroDiaMes(
                mesSelecionado.ano,
                mesSelecionado.mes
            ),
            dataFinal: ultimoDiaMes(
                mesSelecionado.ano,
                mesSelecionado.mes
            )
        })
            .then(res => {
                setJornadas(res.data || []);
            })
            .finally(() => setLoading(false));

    }, [mesSelecionado, funcionarioId]);

    // ── Totalizador do período ────────────────────────────────────────────────
    const totais = useMemo(() => {
        return jornadas.reduce((acc, j) => ({
            horasTrabalhadas: acc.horasTrabalhadas + (j.horasTrabalhadas ?? 0),
            horasPrevistas: acc.horasPrevistas + (j.horasPrevistas ?? 0),
            saldoHoras: acc.saldoHoras + (j.saldoHoras ?? 0),
            saldoBancoHoras: acc.saldoBancoHoras + (j.saldoBancoHoras ?? 0),
            horasExtras: acc.horasExtras + ((j.horaExtra50 ?? 0) + (j.horaExtra60 ?? 0) + (j.horaExtra80 ?? 0) + (j.horaExtra100 ?? 0)),
            horasNoturnas: acc.horasNoturnas + (j.horasNoturnas ?? 0),
            quantidadeFaltas: acc.quantidadeFaltas + (j.quantidadeFaltas ?? 0),
        }), { horasTrabalhadas: 0, horasPrevistas: 0, saldoHoras: 0, saldoBancoHoras: 0, horasExtras: 0, horasNoturnas: 0, quantidadeFaltas: 0 });
    }, [jornadas]);

    // ── Skeleton ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="space-y-2">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-card border border-border rounded-2xl p-4 animate-pulse">
                        <div className="flex gap-3 items-center">
                            <div className="w-12 h-12 bg-muted rounded-xl" />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 bg-muted rounded w-1/3" />
                                <div className="h-3 bg-muted rounded w-1/4" />
                            </div>
                            <div className="h-6 w-16 bg-muted rounded-full" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const funcionario = jornadas[0] ?? {};

    return (
        <div>
            <div className="bg-card sticky top-0 z-[1000] border-bottom border-border rounded-2xl px-4 py-3 shadow-card flex flex-wrap items-center gap-4  ">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0">
                    {funcionario.funcionarioNome?.charAt(0) ?? "?"}
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground truncate">{funcionario.funcionarioNome}</p>
                    <p className="text-xs text-muted-foreground truncate">
                        {funcionario.funcionarioCargo} · {funcionario.funcionarioFilialNome}
                        <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground font-mono">
                            #{funcionario.funcionarioCodigo}
                        </span>
                    </p>
                </div>

                <div className="flex flex-wrap gap-4 text-xs">

                    <span onClick={onClose} className="px-2 rounded-full bg-muted text-muted-foreground font-mono cursor-pointer">
                        < i className="fa fa-times text-lg " />
                    </span>
                </div>
            </div>
            <div className="space-y-3 overflow-y-auto max-h-[36vw] p-3 px-4">

                <div className="bg-card border border-border rounded-2xl p-3 shadow-card">

                    <div className="flex flex-wrap items-center gap-3">

                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">
                                Mês
                            </label>

                            <select
                                value={mesSelecionado.mes}
                                onChange={(e) =>
                                    setMesSelecionado(prev => ({
                                        ...prev,
                                        mes: Number(e.target.value)
                                    }))
                                }
                                className="border border-border rounded-xl px-3 py-2 text-sm"
                            >
                                <option value={1}>Janeiro</option>
                                <option value={2}>Fevereiro</option>
                                <option value={3}>Março</option>
                                <option value={4}>Abril</option>
                                <option value={5}>Maio</option>
                                <option value={6}>Junho</option>
                                <option value={7}>Julho</option>
                                <option value={8}>Agosto</option>
                                <option value={9}>Setembro</option>
                                <option value={10}>Outubro</option>
                                <option value={11}>Novembro</option>
                                <option value={12}>Dezembro</option>
                            </select>
                        </div>

                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">
                                Ano
                            </label>

                            <select
                                value={mesSelecionado.ano}
                                onChange={(e) =>
                                    setMesSelecionado(prev => ({
                                        ...prev,
                                        ano: Number(e.target.value)
                                    }))
                                }
                                className="border border-border rounded-xl px-3 py-2 text-sm"
                            >
                                {[2024, 2025, 2026, 2027].map(ano => (
                                    <option key={ano} value={ano}>
                                        {ano}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="ml-auto text-right">
                            <div className="text-xs text-muted-foreground">
                                Período
                            </div>

                            <div className="text-sm font-semibold text-foreground">
                                {primeiroDiaMes(mesSelecionado.ano, mesSelecionado.mes)}
                                {" até "}
                                {ultimoDiaMes(mesSelecionado.ano, mesSelecionado.mes)}
                            </div>
                        </div>

                    </div>

                </div>
                {(!jornadas.length || jornadas.length === 0) ?
                    <div className="bg-card border border-border rounded-2xl p-12 text-center shadow-card">
                        <p className="text-3xl mb-2">📭</p>
                        <p className="text-sm font-semibold text-muted-foreground">Nenhuma jornada encontrada</p>
                        <p className="text-xs text-muted-foreground mt-1">Ajuste os filtros e pesquise novamente.</p>
                    </div>
                    :
                    <>
                        {/* ── Totalizador do período ────────────────────────────────── */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 z-50">
                            {[
                                { label: "Trabalhadas", value: mth(totais.horasTrabalhadas), icon: "⏱️", color: "bg-emerald-500" },
                                { label: "Previstas", value: mth(totais.horasPrevistas), icon: "📋", color: "bg-primary" },
                                { label: "Saldo", value: mth(totais.saldoHoras), icon: "⚖️", color: totais.saldoHoras >= 0 ? "bg-sky-500" : "bg-red-500" },
                                { label: "Banco", value: mth(totais.saldoBancoHoras), icon: "🏦", color: totais.saldoBancoHoras >= 0 ? "bg-sky-500" : "bg-red-500" },
                                { label: "H. Extras", value: mth(totais.horasExtras), icon: "⚡", color: "bg-amber-500" },
                                { label: "H. Noturnas", value: mth(totais.horasNoturnas), icon: "🌙", color: "bg-violet-500" },
                                { label: "Faltas", value: mth(totais.quantidadeFaltas), icon: "❌", color: "bg-red-500" },
                            ].map(({ label, value, icon, color }) => (
                                <div key={label} className="bg-card border border-border rounded-2xl p-3 shadow-card relative overflow-hidden">
                                    <div className={`absolute top-0 left-0 right-0 h-0.5 z-100 rounded-t-2xl ${color}`} />
                                    <p className="text-base mb-1">{icon}</p>
                                    <p className="text-lg font-bold text-foreground leading-none">{value}</p>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
                                </div>
                            ))}
                        </div>

                        {/* ── Divisor ───────────────────────────────────────────────── */}
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                {jornadas.length} registros
                            </span>
                            <div className="flex-1 h-px bg-border" />
                        </div>

                        {/* ── Cards de jornada (expandíveis) ───────────────────────── */}
                        <div className="space-y-2">
                            {jornadas.map((j) => {
                                const aberto = expandido === j.id;
                                const extrasTotal = (j.horaExtra50 ?? 0) + (j.horaExtra60 ?? 0) + (j.horaExtra80 ?? 0) + (j.horaExtra100 ?? 0);
                                const diaLabel = diaSemana(j.dataReferencia);
                                const mesLabel = mes(j.dataReferencia);
                                const isWeekend = ["Dom", "Sáb"].includes(diaLabel);

                                return (
                                    <div
                                        key={j.id}
                                        className={`bg-card border rounded-2xl shadow-card overflow-hidden transition-all ${aberto ? "border-primary/30" : "border-border"
                                            }`}
                                    >
                                        {/* ── Linha resumo (sempre visível) ─────────── */}
                                        <button
                                            onClick={() => setExpandido(aberto ? null : j.id)}
                                            className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-3 hover:bg-muted transition-colors"
                                        >
                                            {/* Data */}
                                            <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0 ${j.feriado ? "bg-violet-500 text-white"
                                                : j.falta ? "bg-red-50 border border-red-200"
                                                    : isWeekend ? "bg-muted"
                                                        : "bg-indigo-50"
                                                }`}>
                                                <span className={`text-[10px] text-center font-bold uppercase ${j.feriado ? "text-white/80"
                                                    : j.falta ? "text-red-400"
                                                        : isWeekend ? "text-muted-foreground"
                                                            : "text-indigo-400"
                                                    }`}><span className="text-[8px]">{mesLabel}</span>  <br />{diaLabel}</span>
                                                <span className={`text-sm font-extrabold leading-none ${j.feriado ? "text-white"
                                                    : j.falta ? "text-red-500"
                                                        : isWeekend ? "text-muted-foreground"
                                                            : "text-indigo-600"
                                                    }`}>{j.dataReferencia?.split("-")[2]}</span>
                                            </div>

                                            {/* Data completa + tipo */}
                                            <div className="w-24 shrink-0">
                                                <p className="text-xs font-semibold text-foreground">{fmtData(j.dataReferencia)}</p>
                                            </div>

                                            {/* Horas trabalhadas × previstas */}
                                            <div className="flex items-center gap-1.5 min-w-[110px]">
                                                <span className="text-xs font-bold text-foreground">{mth(j.horasTrabalhadas)}</span>
                                                <span className="text-muted-foreground">/</span>
                                                <span className="text-xs text-muted-foreground">{mth(j.horasPrevistas)}</span>
                                            </div>

                                            {/* Saldo */}
                                            <div className={`text-xs font-bold px-2 py-0.5 rounded-full border ${bgSaldo(j.saldoHoras)}`}>
                                                {j.saldoHoras > 0 ? "+" : ""}{mth(j.saldoHoras)}
                                            </div>

                                            {j.tipoMovimento && (
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${tipoMovimentoBadge(j.tipoMovimento)}`}>
                                                    {j.tipoMovimento}
                                                </span>
                                            )}

                                            {/* Badges opcionais */}
                                            <div className="flex flex-wrap gap-1.5 flex-1">
                                                {j.feriado && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200">
                                                        🎉 Feriado
                                                    </span>
                                                )}
                                                {j.falta && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">
                                                        ❌ Falta
                                                    </span>
                                                )}
                                                {j.ajuste && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                                                        ✏️ Ajuste
                                                    </span>
                                                )}
                                                {extrasTotal > 0 && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                                                        ⚡ {mth(extrasTotal)} extras
                                                    </span>
                                                )}
                                                {(j.horasSobreaviso ?? 0) > 0 && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200">
                                                        📡 Sobreaviso
                                                    </span>
                                                )}
                                            </div>

                                            {/* Chevron */}
                                            <i className={`fa fa-chevron-down text-muted-foreground text-xs transition-transform duration-200 ${aberto ? "rotate-180" : ""}`} />
                                        </button>

                                        {/* ── Detalhe expandido ─────────────────────── */}
                                        {aberto && (
                                            <div className="border-t border-border bg-muted px-5 py-4">
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">

                                                    {/* Horas */}
                                                    <DetalheGrupo titulo="Horas" itens={[
                                                        { label: "Trabalhadas", value: mth(j.horasTrabalhadas) },
                                                        { label: "Previstas", value: mth(j.horasPrevistas) },
                                                        { label: "Restantes", value: mth(j.horasRestantes) },
                                                        { label: "Noturnas", value: mth(j.horasNoturnas) },
                                                        { label: "Fictas", value: mth(j.horasFictas) },
                                                        { label: "Pagas", value: mth(j.horasPagas) },
                                                    ]} />

                                                    {/* Saldos */}
                                                    <DetalheGrupo titulo="Saldos" itens={[
                                                        { label: "Saldo Horas", value: mth(j.saldoHoras), destaque: corSaldo(j.saldoHoras) },
                                                        { label: "Saldo Calculado", value: mth(j.saldoHorasCalculado), destaque: corSaldo(j.saldoHorasCalculado) },
                                                        { label: "Banco de Horas", value: mth(j.saldoBancoHoras), destaque: corSaldo(j.saldoBancoHoras) },
                                                        { label: "Banco Excedente", value: mth(j.saldoBancoHorasExcedente), destaque: corSaldo(j.saldoBancoHorasExcedente) },
                                                    ]} />

                                                    {/* Extras */}
                                                    <DetalheGrupo titulo="Horas Extras" itens={[
                                                        { label: "50%", value: mth(j.horaExtra50), destaque: (j.horaExtra50 ?? 0) > 0 ? "text-amber-500" : "" },
                                                        { label: "60%", value: mth(j.horaExtra60), destaque: (j.horaExtra60 ?? 0) > 0 ? "text-orange-500" : "" },
                                                        { label: "80%", value: mth(j.horaExtra80), destaque: (j.horaExtra80 ?? 0) > 0 ? "text-orange-600" : "" },
                                                        { label: "100%", value: mth(j.horaExtra100), destaque: (j.horaExtra100 ?? 0) > 0 ? "text-red-500" : "" },
                                                        { label: "Total", value: mth(extrasTotal), destaque: extrasTotal > 0 ? "text-amber-600 font-bold" : "" },
                                                    ]} />

                                                    {/* Sobreaviso */}
                                                    <DetalheGrupo titulo="Sobreaviso" itens={[
                                                        { label: "Previsto", value: mth(j.horasSobreaviso) },
                                                        { label: "Trabalhado", value: mth(j.horasSobreavisoTrabalhadas) },
                                                    ]} />

                                                    {/* Status / Info */}
                                                    <DetalheGrupo titulo="Status" itens={[
                                                        { label: "Falta", value: j.falta ? "Sim" : "Não", destaque: j.falta ? "text-red-500" : "text-muted-foreground" },
                                                        { label: "Qtd. Faltas", value: j.quantidadeFaltas ?? 0 },
                                                        { label: "Feriado", value: j.feriado ? "Sim" : "Não", destaque: j.feriado ? "text-violet-500" : "text-muted-foreground" },
                                                        { label: "Ajuste", value: j.ajuste ? "Sim" : "Não", destaque: j.ajuste ? "text-amber-500" : "text-muted-foreground" },
                                                        { label: "Tipo", value: j.tipoMovimento ?? "—" },
                                                    ]} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                }
            </div>
        </div>
    );
}

export default JornadaFuncionarioList;

// ── Sub-componente: grupo de campos no detalhe expandido ─────────────────────
function DetalheGrupo({ titulo, itens }) {
    return (
        <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 border-b border-border pb-1.5">
                {titulo}
            </p>
            <div className="space-y-1.5">
                {itens.map(({ label, value, destaque }) => (
                    <div key={label} className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground shrink-0">{label}</span>
                        <span className={`text-[11px] font-semibold text-right ${destaque || "text-foreground"}`}>
                            {value ?? "—"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}